import React, { useState } from "react";
import analyticsService from "../../../services/analyticsService";
import "./RiskSentiment.scss";

interface NewsHeadline {
  title: string;
  date_ago?: string;
  date?: string;
  sentiment: "positive" | "negative" | "neutral";
  source: string;
}

interface InsiderTrade {
  name: string;
  role: string;
  action: "buy" | "sell";
  shares: string;
  value: string;
  date: string;
}

interface AnalystRating {
  firm: string;
  rating: string;
  targetPrice: number;
  date: string;
  change: "upgrade" | "downgrade" | "maintained";
}

interface RiskSentimentData {
  news: NewsHeadline[];
  insiderTrades: InsiderTrade[];
  promoterHoldingChange: {
    currentHolding: number;
    previousHolding: number;
    changeQoQ: number;
  } | null;
  analystConsensus: {
    averageTarget: number;
    upside: number;
    buyRatings: number;
    holdRatings: number;
    sellRatings: number;
  } | null;
  brokerageSignals: AnalystRating[];
}

interface RiskSentimentProps {
  companyId?: number;
  company?: any;
  analytics?: any;
  data?: RiskSentimentData;
  onRefresh?: () => void;
}

const RiskSentiment: React.FC<RiskSentimentProps> = ({ companyId, company, analytics, data, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    if (!companyId) return;
    setRefreshing(true);

    // Refresh both news and fundamentals data
    const newsRefresh = analyticsService.refreshNewsSentiment(companyId);
    const fundamentalsRefresh = analyticsService.refreshFundamentals(companyId);

    // Wait for both to complete
    let newsComplete = false;
    let fundamentalsComplete = false;

    const checkCompletion = () => {
      if (newsComplete && fundamentalsComplete) {
        if (onRefresh) onRefresh();
        setRefreshing(false);
      }
    };

    newsRefresh.subscribe({
      next: () => {
        newsComplete = true;
        checkCompletion();
      },
      error: (err) => {
        console.error("Failed to refresh news:", err);
        newsComplete = true;
        checkCompletion();
      }
    });

    fundamentalsRefresh.subscribe({
      next: () => {
        fundamentalsComplete = true;
        checkCompletion();
      },
      error: (err) => {
        console.error("Failed to refresh fundamentals:", err);
        fundamentalsComplete = true;
        checkCompletion();
      }
    });
  };
  // Mock data - will be used as fallback
  const mockData: RiskSentimentData = {
    news: [
      {
        title: "Company announces 25% revenue growth in Q3",
        date: "2 days ago",
        sentiment: "positive",
        source: "Economic Times"
      },
      {
        title: "New product launch expected to boost margins",
        date: "5 days ago",
        sentiment: "positive",
        source: "Business Standard"
      },
      {
        title: "Regulatory concerns may impact near-term operations",
        date: "1 week ago",
        sentiment: "negative",
        source: "Reuters"
      },
      {
        title: "Strategic partnership signed with global tech firm",
        date: "2 weeks ago",
        sentiment: "positive",
        source: "Mint"
      },
      {
        title: "Industry outlook remains mixed amid economic headwinds",
        date: "3 weeks ago",
        sentiment: "neutral",
        source: "Bloomberg"
      }
    ],
    insiderTrades: [
      {
        name: "Rajesh Kumar",
        role: "CEO",
        action: "buy",
        shares: "50,000",
        value: "₹4.2 Cr",
        date: "15 Dec 2024"
      },
      {
        name: "Priya Sharma",
        role: "CFO",
        action: "buy",
        shares: "25,000",
        value: "₹2.1 Cr",
        date: "10 Dec 2024"
      },
      {
        name: "Amit Patel",
        role: "Director",
        action: "sell",
        shares: "10,000",
        value: "₹84 L",
        date: "5 Dec 2024"
      }
    ],
    promoterHoldingChange: {
      currentHolding: 52.3,
      previousHolding: 51.8,
      changeQoQ: 0.5
    },
    analystConsensus: {
      averageTarget: 1000,
      upside: 19.4,
      buyRatings: 12,
      holdRatings: 5,
      sellRatings: 2
    },
    brokerageSignals: [
      {
        firm: "Morgan Stanley",
        rating: "Equal-Weight",
        targetPrice: 950,
        date: "20 Dec 2024",
        change: "maintained"
      },
      {
        firm: "Goldman Sachs",
        rating: "Buy",
        targetPrice: 1050,
        date: "18 Dec 2024",
        change: "upgrade"
      },
      {
        firm: "ICICI Securities",
        rating: "Buy",
        targetPrice: 1020,
        date: "15 Dec 2024",
        change: "maintained"
      }
    ]
  };

  // Transform analytics data to RiskSentimentData format
  const getDataFromAnalytics = (): RiskSentimentData => {
    if (!analytics) {
      return mockData;
    }

    // Transform news
    const news = analytics.news?.length > 0
      ? analytics.news.map((item: any) => ({
          title: item.title,
          date_ago: item.date_ago,
          date: item.date,
          sentiment: item.sentiment as "positive" | "negative" | "neutral",
          source: item.source
        }))
      : mockData.news;

    // Transform insider trades - don't use mock data
    const insiderTrades = analytics.insider_trades?.length > 0
      ? analytics.insider_trades.map((trade: any) => ({
          name: trade.name,
          role: trade.role,
          action: trade.action as "buy" | "sell",
          shares: trade.shares,
          value: trade.value,
          date: trade.date
        }))
      : [];

    // Transform promoter holdings - don't use mock data
    const promoterHoldingChange = analytics.promoter_holdings
      ? {
          currentHolding: analytics.promoter_holdings.current_holding || 0,
          previousHolding: analytics.promoter_holdings.previous_holding || 0,
          changeQoQ: analytics.promoter_holdings.change_qoq || 0
        }
      : null;

    // Transform analyst ratings - don't use mock data
    const brokerageSignals = analytics.analyst_ratings?.ratings?.length > 0
      ? analytics.analyst_ratings.ratings.map((rating: any) => ({
          firm: rating.firm,
          rating: rating.rating,
          targetPrice: rating.target_price || 0,
          date: rating.date,
          change: rating.change as "upgrade" | "downgrade" | "maintained"
        }))
      : [];

    // Calculate analyst consensus - don't use mock data
    const analystConsensus = analytics.analyst_ratings?.consensus &&
                             (analytics.analyst_ratings.consensus.buy_ratings > 0 ||
                              analytics.analyst_ratings.consensus.hold_ratings > 0 ||
                              analytics.analyst_ratings.consensus.sell_ratings > 0)
      ? {
          averageTarget: analytics.analyst_ratings.consensus.average_target || 0,
          upside: analytics.analyst_ratings.consensus.average_target && analytics.performance?.current_price
            ? ((analytics.analyst_ratings.consensus.average_target - analytics.performance.current_price) / analytics.performance.current_price) * 100
            : 0,
          buyRatings: analytics.analyst_ratings.consensus.buy_ratings || 0,
          holdRatings: analytics.analyst_ratings.consensus.hold_ratings || 0,
          sellRatings: analytics.analyst_ratings.consensus.sell_ratings || 0
        }
      : null;

    return {
      news,
      insiderTrades,
      promoterHoldingChange,
      analystConsensus,
      brokerageSignals
    };
  };

  const sentimentData = data || (analytics ? getDataFromAnalytics() : mockData);

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return <span className="icon has-text-success"><i className="fas fa-smile"></i></span>;
      case "negative":
        return <span className="icon has-text-danger"><i className="fas fa-frown"></i></span>;
      case "neutral":
        return <span className="icon has-text-warning"><i className="fas fa-meh"></i></span>;
      default:
        return null;
    }
  };

  const getSentimentTag = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return <span className="tag is-success is-light">Positive</span>;
      case "negative":
        return <span className="tag is-danger is-light">Negative</span>;
      case "neutral":
        return <span className="tag is-warning is-light">Neutral</span>;
      default:
        return null;
    }
  };

  const getActionTag = (action: string) => {
    return action === "buy" ? (
      <span className="tag is-success">Buy</span>
    ) : (
      <span className="tag is-danger">Sell</span>
    );
  };

  const getChangeTag = (change: string) => {
    switch (change) {
      case "upgrade":
        return <span className="tag is-success">Upgrade</span>;
      case "downgrade":
        return <span className="tag is-danger">Downgrade</span>;
      case "maintained":
        return <span className="tag is-info">Maintained</span>;
      default:
        return null;
    }
  };

  return (
    <div className="risk-sentiment">
      <div className="box">
        <div className="is-flex is-justify-content-space-between is-align-items-center mb-5">
          <div>
            <h2 className="title is-4 mb-2">Risk & Sentiment Analysis</h2>
            <p className="subtitle is-6 has-text-grey">
              Market sentiment and risk indicators to avoid surprises
            </p>
          </div>
          <button
            className={`button is-small is-info ${refreshing ? 'is-loading' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh news and sentiment"
          >
            <span className="icon is-small">
              <i className="fas fa-sync-alt"></i>
            </span>
            <span>Refresh</span>
          </button>
        </div>

        <div className="columns is-variable is-4">
          {/* Recent News Headlines */}
          <div className="column is-5">
            <div className="card">
              <div className="card-header">
                <p className="card-header-title">
                  <span className="icon mr-2">
                    <i className="fas fa-newspaper"></i>
                  </span>
                  Recent News Headlines
                </p>
              </div>
              <div className="card-content">
                <p className="subtitle is-7 has-text-grey mb-4">Catalysts and risks</p>
                <div className="news-list">
                  {sentimentData.news.map((item, index) => (
                    <div key={index} className="news-item mb-4">
                      <div className="media">
                        <div className="media-left">
                          {getSentimentIcon(item.sentiment)}
                        </div>
                        <div className="media-content">
                          <p className="has-text-weight-semibold mb-1">{item.title}</p>
                          <p className="is-size-7 has-text-grey">
                            <span>{item.source}</span>
                            <span className="mx-2">•</span>
                            <span>{item.date_ago || item.date}</span>
                          </p>
                        </div>
                        <div className="media-right">
                          {getSentimentTag(item.sentiment)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Insider Trades & Promoter Holding */}
          <div className="column is-4">
            {/* Insider Trades */}
            <div className="card mb-4">
              <div className="card-header">
                <p className="card-header-title">
                  <span className="icon mr-2">
                    <i className="fas fa-user-tie"></i>
                  </span>
                  Insider Trades
                </p>
              </div>
              <div className="card-content">
                <p className="subtitle is-7 has-text-grey mb-4">Management confidence</p>
                {sentimentData.insiderTrades.length > 0 ? (
                  <div className="table-container">
                    <table className="table is-fullwidth is-size-7">
                      <thead>
                        <tr>
                          <th>Name (Role)</th>
                          <th>Action</th>
                          <th>Shares</th>
                          <th>Value</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sentimentData.insiderTrades.map((trade, index) => (
                          <tr key={index}>
                            <td>
                              <div>
                                <p className="has-text-weight-semibold">{trade.name}</p>
                                <p className="has-text-grey is-size-7">{trade.role}</p>
                              </div>
                            </td>
                            <td>{getActionTag(trade.action)}</td>
                            <td>{trade.shares}</td>
                            <td>{trade.value}</td>
                            <td className="has-text-grey">{trade.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="has-text-centered has-text-grey py-5">
                    <p className="is-size-7">No insider trading data available</p>
                    <p className="is-size-7 mt-2">Click Refresh to fetch latest data</p>
                  </div>
                )}
              </div>
            </div>
             </div>

          {/* Promoter Holding */}
          <div className="column is-3">

            {/* Promoter Holding */}
            <div className="card promoter-holding-card">
              <div className="card-header">
                <p className="card-header-title">
                  <span className="icon mr-2">
                    <i className="fas fa-chart-pie"></i>
                  </span>
                  Promoter Holding
                </p>
              </div>
              <div className="card-content">
                <p className="subtitle is-6 has-text-grey mb-4">Long-term faith indicator</p>
                {sentimentData.promoterHoldingChange ? (
                  <div className="promoter-stats">
                    <div className="stat-item">
                      <p className="stat-label">Current Holding</p>
                      <p className="stat-value has-text-primary">{sentimentData.promoterHoldingChange.currentHolding}%</p>
                    </div>
                    <div className="stat-item">
                      <p className="stat-label">Previous Quarter</p>
                      <p className="stat-value">{sentimentData.promoterHoldingChange.previousHolding}%</p>
                    </div>
                    <div className="stat-item">
                      <p className="stat-label">QoQ Change</p>
                      <p className={`stat-value stat-change ${sentimentData.promoterHoldingChange.changeQoQ >= 0 ? 'positive' : 'negative'}`}>
                        <span className="icon is-small mr-1">
                          <i className={`fas fa-arrow-${sentimentData.promoterHoldingChange.changeQoQ >= 0 ? 'up' : 'down'}`}></i>
                        </span>
                        {sentimentData.promoterHoldingChange.changeQoQ >= 0 ? '+' : ''}{sentimentData.promoterHoldingChange.changeQoQ}%
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="has-text-centered has-text-grey py-5">
                    <p className="is-size-7">No promoter holding data available</p>
                    <p className="is-size-7 mt-2">Click Refresh to fetch latest data</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Analyst Ratings & Brokerage Signals */}
        <div className="columns is-variable is-4 mt-4">
          {/* Analyst Consensus */}
          <div className="column is-4">
            <div className="card analyst-consensus-card">
              <div className="card-header">
                <p className="card-header-title">
                  <span className="icon mr-2">
                    <i className="fas fa-users"></i>
                  </span>
                  Analyst Consensus
                </p>
              </div>
              <div className="card-content has-text-centered">
                <p className="subtitle is-7 has-text-grey mb-4">Average target price</p>
                {sentimentData.analystConsensus ? (
                  <>
                    <p className="title is-2 mb-2">₹{sentimentData.analystConsensus.averageTarget}</p>
                    <p className="subtitle is-5 has-text-success mb-4">
                      Upside: {sentimentData.analystConsensus.upside.toFixed(1)}%
                    </p>
                    <div className="ratings-distribution">
                      <div className="columns is-mobile is-gapless">
                        <div className="column">
                          <p className="has-text-success has-text-weight-bold is-size-3">{sentimentData.analystConsensus.buyRatings}</p>
                          <p className="is-size-7 has-text-grey">Buy</p>
                        </div>
                        <div className="column">
                          <p className="has-text-warning has-text-weight-bold is-size-3">{sentimentData.analystConsensus.holdRatings}</p>
                          <p className="is-size-7 has-text-grey">Hold</p>
                        </div>
                        <div className="column">
                          <p className="has-text-danger has-text-weight-bold is-size-3">{sentimentData.analystConsensus.sellRatings}</p>
                          <p className="is-size-7 has-text-grey">Sell</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="has-text-grey py-5">
                    <p className="is-size-7">No analyst consensus available</p>
                    <p className="is-size-7 mt-2">Click Refresh to fetch latest data</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Brokerage Signals */}
          <div className="column is-8">
            <div className="card">
              <div className="card-header">
                <p className="card-header-title">
                  <span className="icon mr-2">
                    <i className="fas fa-building"></i>
                  </span>
                  Recent Brokerage Signals
                </p>
              </div>
              <div className="card-content">
                <p className="subtitle is-7 has-text-grey mb-4">Recent upgrades/downgrades</p>
                {sentimentData.brokerageSignals.length > 0 ? (
                  <div className="table-container">
                    <table className="table is-fullwidth is-hoverable">
                      <thead>
                        <tr>
                          <th>Brokerage Firm</th>
                          <th>Rating</th>
                          <th>Target Price</th>
                          <th>Change</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sentimentData.brokerageSignals.map((signal, index) => (
                          <tr key={index}>
                            <td className="has-text-weight-semibold">{signal.firm}</td>
                            <td>
                              <span className="tag is-info is-light">{signal.rating}</span>
                            </td>
                            <td className="has-text-weight-semibold">₹{signal.targetPrice}</td>
                            <td>{getChangeTag(signal.change)}</td>
                            <td className="has-text-grey">{signal.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="has-text-centered has-text-grey py-5">
                    <p className="is-size-7">No brokerage signals available</p>
                    <p className="is-size-7 mt-2">Click Refresh to fetch latest data</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskSentiment;
